import type { ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import { onboardingTransactionFieldSx } from './onboardingFormStyles';
import {
  useTransactionFormUi,
  useTransactionGridSpacing,
} from './TransactionFormUi';
import { TransactionStepHeader } from './TransactionStepHeader';

type TransactionStepShellProps = {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  drawerPadding?: boolean;
  withFieldsGrid?: boolean;
};

export const TransactionStepShell = ({
  title,
  subtitle,
  children,
  drawerPadding = true,
  withFieldsGrid = false,
}: TransactionStepShellProps) => {
  const variant = useTransactionFormUi();
  const gridSpacing = useTransactionGridSpacing();

  if (variant === 'onboarding') {
    return (
      <Stack
        spacing={2}
        className="onboarding-transaction-step"
        sx={{
          p: { xs: 1.5, md: 2 },
          ...onboardingTransactionFieldSx,
        }}
      >
        {title != null && (
          <Box>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                letterSpacing: 1,
                fontWeight: 700,
                fontSize: 11,
                color: 'text.secondary',
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
            {subtitle != null && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, lineHeight: 1.45 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
        {withFieldsGrid ? (
          <Grid
            container
            spacing={gridSpacing}
            sx={{ alignContent: 'flex-start' }}
          >
            {children}
          </Grid>
        ) : (
          children
        )}
      </Stack>
    );
  }

  if (withFieldsGrid) {
    return (
      <Grid
        container
        spacing={gridSpacing}
        sx={{ alignContent: 'flex-start', p: 2 }}
      >
        {title != null && (
          <Grid size={12}>
            <TransactionStepHeader title={title} subtitle={subtitle} />
          </Grid>
        )}
        {children}
      </Grid>
    );
  }

  return (
    <>
      {title != null && drawerPadding && (
        <Box sx={{ px: 2, pt: 2, pb: 0 }}>
          <TransactionStepHeader title={title} subtitle={subtitle} />
        </Box>
      )}
      {children}
    </>
  );
};
